import { useCallback } from 'react';

import { Tasks } from '@air/api';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type DuplicateAssetTask, type GetBaseMetadata } from '~/store/tasks/types';
import { useCreateTask } from '~/utils/taskUtils/useCreateTask';

export type CreateDuplicateClipTaskParams = Omit<GetBaseMetadata<DuplicateAssetTask>, 'workspaceId'>;

export const useCreateDuplicateClipTask = () => {
  const { createTask } = useCreateTask();
  const { currentWorkspace } = useCurrentWorkspace();

  const createDuplicateClipTask = useCallback(
    async ({ sourceAssetId, sourceItemTitle, targetBoardId, isFile, trackLocation }: CreateDuplicateClipTaskParams) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const result = await createTask({
        createTask: () =>
          Tasks.createTask({
            task: {
              args: {
                prefix: 'Copy of ',
                sourceAssetIds: [sourceAssetId],
                targetBoardId,
              },
              type: 'ContentDuplicator',
            },
            workspaceId,
          }),
        task: {
          metadata: { isFile, sourceAssetId, sourceItemTitle, targetBoardId, trackLocation, workspaceId },
          type: 'DuplicateAsset',
          workspaceId,
        },
      });

      if (result.error) {
        throw result.error;
      }

      return result;
    },
    [createTask, currentWorkspace?.id],
  );

  return {
    createDuplicateClipTask,
  };
};
